import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Bell,
  CheckCircle2,
  Circle,
  CircleAlert,
  Clock3,
  FileText,
  KeyRound,
  LayoutList,
  LogOut,
  Mail,
  PlusSquare,
  ShieldCheck,
  Ticket,
  Upload,
  UserRound,
  Users,
} from "lucide-react";

import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { useToast } from "../hooks/use-toast";
import Header from "./Header";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "support_access_token";

const initialRegister = {
  full_name: "",
  company_name: "",
  email: "",
  password: "",
};

const initialLogin = {
  email: "",
  password: "",
};

const initialForgotPassword = {
  email: "",
};

const initialResetPassword = {
  email: "",
  token: "",
  new_password: "",
};

const initialTicket = {
  title: "",
  category: "modification-contenu",
  site_url: "",
  description: "",
};

const STATUS_OPTIONS = [
  { value: "ticket_ouvert", label: "Ticket ouvert" },
  { value: "en_cours_de_traitement", label: "En cours de traitement" },
  { value: "traite", label: "Traite" },
  { value: "demande_info_supplementaires", label: "Demande d'informations supplementaires" },
];

const getPasswordChecks = (password) => [
  { label: "Au moins 10 caracteres", valid: password.length >= 10 },
  { label: "Une majuscule", valid: /[A-Z]/.test(password) },
  { label: "Une minuscule", valid: /[a-z]/.test(password) },
  { label: "Un chiffre", valid: /\d/.test(password) },
];

const getPasswordStrength = (password) => {
  const checks = getPasswordChecks(password);
  const passed = checks.filter((item) => item.valid).length;

  if (!password) {
    return { label: "A completer", tone: "neutral", checks };
  }
  if (passed === checks.length) {
    return { label: "Mot de passe acceptable", tone: "success", checks };
  }
  if (passed >= 2) {
    return { label: "Presque bon", tone: "warning", checks };
  }
  return { label: "Trop faible", tone: "danger", checks };
};

const getApiErrorMessage = (error, fallbackMessage) => {
  const detail = error?.response?.data?.detail;

  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (typeof item === "string") {
          return item;
        }
        if (item?.loc?.length) {
          const fieldName = item.loc[item.loc.length - 1];
          return `${fieldName} : ${item.msg}`;
        }
        return item?.msg || "Erreur de validation.";
      })
      .join(" | ");
  }

  if (typeof detail === "string") {
    return detail;
  }

  if (typeof error?.message === "string" && !error.response) {
    return error.message;
  }

  return fallbackMessage;
};

function PasswordRequirements({ password }) {
  const strength = getPasswordStrength(password);

  return (
    <div className="support-password-box">
      <div className={`support-password-status is-${strength.tone}`}>
        <KeyRound className="w-4 h-4" />
        {strength.label}
      </div>
      <div className="support-password-rules">
        {strength.checks.map((check) => (
          <div key={check.label} className={`support-password-rule ${check.valid ? "is-valid" : ""}`}>
            {check.valid ? <CheckCircle2 className="w-4 h-4" /> : <Circle className="w-4 h-4" />}
            <span>{check.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SupportPortal() {
  const { toast } = useToast();
  const [mode, setMode] = useState("login");
  const [activeView, setActiveView] = useState("tickets");
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [user, setUser] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [adminClients, setAdminClients] = useState([]);
  const [selectedTicketId, setSelectedTicketId] = useState("");
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [registerForm, setRegisterForm] = useState(initialRegister);
  const [loginForm, setLoginForm] = useState(initialLogin);
  const [forgotPasswordForm, setForgotPasswordForm] = useState(initialForgotPassword);
  const [resetPasswordForm, setResetPasswordForm] = useState(initialResetPassword);
  const [ticketForm, setTicketForm] = useState(initialTicket);
  const [ticketFiles, setTicketFiles] = useState([]);
  const [messageDraft, setMessageDraft] = useState("");
  const [adminStatus, setAdminStatus] = useState("ticket_ouvert");
  const [adminComment, setAdminComment] = useState("");

  const authHeaders = useMemo(
    () => ({
      headers: { Authorization: `Bearer ${token}` },
    }),
    [token]
  );

  const filteredTickets = useMemo(() => {
    if (!selectedClientId) return tickets;
    return tickets.filter((ticket) => ticket.client_id === selectedClientId);
  }, [tickets, selectedClientId]);

  const formatDate = (value) =>
    new Date(value).toLocaleString("fr-FR", {
      dateStyle: "medium",
      timeStyle: "short",
    });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const routeMode = params.get("mode");
    const email = params.get("email");
    const resetToken = params.get("token");

    if (routeMode === "reset") {
      setMode("reset");
      setResetPasswordForm((current) => ({
        ...current,
        email: email || "",
        token: resetToken || "",
      }));
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setUser(null);
    setTickets([]);
    setAdminClients([]);
    setSelectedTicketId("");
    setSelectedTicket(null);
    setSelectedClientId("");
    setNotifications([]);
    setUnreadCount(0);
    setActiveView("tickets");
  };

  const fetchDashboard = async (authToken = token) => {
    if (!authToken) return;

    setLoadingData(true);
    try {
      const headers = { headers: { Authorization: `Bearer ${authToken}` } };
      const meRes = await axios.get(`${API}/auth/me`, headers);
      const nextUser = meRes.data;
      const [ticketsRes, notifRes] = await Promise.all([
        axios.get(`${API}/tickets`, headers),
        axios.get(`${API}/tickets/notifications/list`, headers),
      ]);
      const nextTickets = ticketsRes.data;

      setUser(nextUser);
      setTickets(nextTickets);
      setNotifications(notifRes.data.items);
      setUnreadCount(notifRes.data.unread_count);

      if (nextUser.role === "admin") {
        try {
          const clientsRes = await axios.get(`${API}/auth/admin/clients`, headers);
          setAdminClients(clientsRes.data);
        } catch (error) {
          setAdminClients([]);
        }
      } else {
        setAdminClients([]);
      }

      if (nextUser.role !== "admin") {
        setSelectedClientId("");
      }

      if (nextTickets.length > 0) {
        const availableIds = nextTickets.map((ticket) => ticket.id);
        const targetId = availableIds.includes(selectedTicketId) ? selectedTicketId : nextTickets[0].id;
        const detailRes = await axios.get(`${API}/tickets/${targetId}`, headers);
        setSelectedTicket(detailRes.data);
        setSelectedTicketId(detailRes.data.id);
        setAdminStatus(detailRes.data.status);
      } else {
        setSelectedTicket(null);
        setSelectedTicketId("");
      }
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoadingData(false);
    }
  };

  useEffect(() => {
    if (!token) return undefined;
    fetchDashboard(token);
    const intervalId = window.setInterval(() => fetchDashboard(token), 30000);
    return () => window.clearInterval(intervalId);
  }, [token]);

  const handleAuthSuccess = async (response) => {
    const nextToken = response.data.access_token;
    localStorage.setItem(TOKEN_KEY, nextToken);
    setToken(nextToken);
    setUser(response.data.user);
    setActiveView("tickets");
    setSelectedClientId("");
    setSelectedTicketId("");
    setSelectedTicket(null);
    setTickets([]);
    setAdminClients([]);
    toast({
      title: "Connexion reussie",
      description: "Votre espace support est disponible.",
    });
    await fetchDashboard(nextToken);
  };

  const handleRegister = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, registerForm);
      await handleAuthSuccess(response);
      setRegisterForm(initialRegister);
    } catch (error) {
      toast({
        title: "Inscription impossible",
        description: getApiErrorMessage(error, "Verifiez les informations saisies."),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, loginForm);
      await handleAuthSuccess(response);
      setLoginForm(initialLogin);
    } catch (error) {
      toast({
        title: "Connexion impossible",
        description: getApiErrorMessage(error, "Identifiants invalides."),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, forgotPasswordForm);
      toast({
        title: "Reinitialisation envoyee",
        description: response.data.message,
      });
      setForgotPasswordForm(initialForgotPassword);
      setMode("login");
    } catch (error) {
      toast({
        title: "Demande impossible",
        description: getApiErrorMessage(error, "Veuillez reessayer."),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/reset-password`, resetPasswordForm);
      toast({
        title: "Mot de passe reinitialise",
        description: response.data.message,
      });
      setResetPasswordForm(initialResetPassword);
      setMode("login");
      window.history.replaceState({}, document.title, "/espace-client");
    } catch (error) {
      toast({
        title: "Reinitialisation impossible",
        description: getApiErrorMessage(error, "Veuillez reessayer."),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const openTicket = async (ticketId) => {
    setSelectedTicketId(ticketId);
    setActiveView("tickets");
    try {
      const response = await axios.get(`${API}/tickets/${ticketId}`, authHeaders);
      setSelectedTicket(response.data);
      setAdminStatus(response.data.status);
    } catch (error) {
      toast({
        title: "Ticket indisponible",
        description: "Impossible de charger ce ticket.",
        variant: "destructive",
      });
    }
  };

  const handleCreateTicket = async (event) => {
    event.preventDefault();
    const formData = new FormData();
    formData.append("title", ticketForm.title);
    formData.append("category", ticketForm.category);
    formData.append("site_url", ticketForm.site_url);
    formData.append("description", ticketForm.description);
    Array.from(ticketFiles).forEach((file) => formData.append("attachments", file));

    setLoading(true);
    try {
      await axios.post(`${API}/tickets`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      setTicketForm(initialTicket);
      setTicketFiles([]);
      setActiveView("tickets");
      await fetchDashboard();
      toast({
        title: "Ticket envoye",
        description: "Votre demande de modification a bien ete transmise.",
      });
    } catch (error) {
      toast({
        title: "Creation impossible",
        description: getApiErrorMessage(error, "Le ticket n'a pas pu etre cree."),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (event) => {
    event.preventDefault();
    if (!selectedTicket || !messageDraft.trim()) return;

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/tickets/${selectedTicket.id}/messages`,
        { content: messageDraft },
        authHeaders
      );
      setSelectedTicket(response.data);
      setMessageDraft("");
      await fetchDashboard();
    } catch (error) {
      toast({
        title: "Message non envoye",
        description: getApiErrorMessage(error, "Veuillez reessayer."),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (event) => {
    event.preventDefault();
    if (!selectedTicket) return;

    setLoading(true);
    try {
      const response = await axios.patch(
        `${API}/tickets/${selectedTicket.id}/status`,
        { status: adminStatus, comment: adminComment || null },
        authHeaders
      );
      setSelectedTicket(response.data);
      setAdminComment("");
      await fetchDashboard();
      toast({
        title: "Statut mis a jour",
        description: "Le client a ete notifie dans l'espace support et par email.",
      });
    } catch (error) {
      toast({
        title: "Mise a jour impossible",
        description: getApiErrorMessage(error, "Veuillez reessayer."),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await axios.patch(`${API}/tickets/notifications/${notificationId}/read`, {}, authHeaders);
      await fetchDashboard();
    } catch (error) {
      toast({
        title: "Notification",
        description: "Impossible de marquer cette notification comme lue.",
        variant: "destructive",
      });
    }
  };

  const downloadAttachment = async (attachment) => {
    if (!selectedTicket) return;

    try {
      const response = await axios.get(
        `${API}/tickets/${selectedTicket.id}/attachments/${attachment.id}`,
        {
          ...authHeaders,
          responseType: "blob",
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = attachment.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast({
        title: "Telechargement impossible",
        description: "La piece jointe n'a pas pu etre recuperee.",
        variant: "destructive",
      });
    }
  };

  const clientMenuItems = [
    { id: "tickets", label: "Tickets", icon: LayoutList },
    { id: "new-ticket", label: "Ajouter un ticket", icon: PlusSquare },
    { id: "notifications", label: "Notifications", icon: Bell, badge: unreadCount },
    { id: "profile", label: "Infos client", icon: UserRound },
  ];

  const adminMenuItems = [
    { id: "tickets", label: "Tickets", icon: LayoutList },
    { id: "clients", label: "Clients", icon: Users },
    { id: "notifications", label: "Notifications", icon: Bell, badge: unreadCount },
    { id: "profile", label: "Infos support", icon: UserRound },
  ];

  const menuItems = user?.role === "admin" ? adminMenuItems : clientMenuItems;

  const renderAuthView = () => (
    <section className="support-hero">
      <div className="container support-hero-grid">
        <div>
          <div className="hero-announcement">
            <ShieldCheck className="w-4 h-4" />
            Support securise
          </div>
          <h1 className="heading-1 mb-4">Espace client et gestion des tickets</h1>
          <p className="body-large">
            Connexion securisee, tickets par site, pieces jointes, suivi des statuts,
            reponses support et centre de notifications interne.
          </p>
          <div className="support-feature-list">
            <div><Ticket className="w-4 h-4" /> Tickets organises</div>
            <div><Upload className="w-4 h-4" /> Pieces jointes</div>
            <div><Bell className="w-4 h-4" /> Alertes internes</div>
          </div>
        </div>

        <Card className="support-auth-card">
          <CardHeader>
            <CardTitle className="heading-2">
              {mode === "login" && "Connexion"}
              {mode === "register" && "Creer un compte client"}
              {mode === "forgot" && "Mot de passe oublie"}
              {mode === "reset" && "Creer un nouveau mot de passe"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="support-switch">
              <button type="button" className={mode === "login" ? "btn-primary" : "btn-secondary"} onClick={() => setMode("login")}>
                Connexion
              </button>
              <button type="button" className={mode === "register" ? "btn-primary" : "btn-secondary"} onClick={() => setMode("register")}>
                Inscription
              </button>
            </div>

            {mode === "login" && (
              <form onSubmit={handleLogin} className="support-form-stack">
                <input className="form-input" type="email" placeholder="Email" value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} required />
                <input className="form-input" type="password" placeholder="Mot de passe" value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} required />
                <button type="button" className="support-text-link" onClick={() => { setForgotPasswordForm({ email: loginForm.email }); setMode("forgot"); }}>
                  Mot de passe oublie ?
                </button>
                <Button type="submit" className="btn-primary w-full" disabled={loading}>
                  {loading ? "Connexion..." : "Se connecter"}
                </Button>
              </form>
            )}

            {mode === "register" && (
              <form onSubmit={handleRegister} className="support-form-stack">
                <input className="form-input" type="text" placeholder="Nom complet" value={registerForm.full_name} onChange={(e) => setRegisterForm({ ...registerForm, full_name: e.target.value })} required />
                <input className="form-input" type="text" placeholder="Nom de l'entreprise" value={registerForm.company_name} onChange={(e) => setRegisterForm({ ...registerForm, company_name: e.target.value })} required />
                <input className="form-input" type="email" placeholder="Email" value={registerForm.email} onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })} required />
                <input className="form-input" type="password" placeholder="Mot de passe fort" value={registerForm.password} onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })} required />
                <PasswordRequirements password={registerForm.password} />
                <Button type="submit" className="btn-primary w-full" disabled={loading}>
                  {loading ? "Creation..." : "Creer mon compte"}
                </Button>
              </form>
            )}

            {mode === "forgot" && (
              <form onSubmit={handleForgotPassword} className="support-form-stack">
                <input className="form-input" type="email" placeholder="Email du compte" value={forgotPasswordForm.email} onChange={(e) => setForgotPasswordForm({ email: e.target.value })} required />
                <p className="body-small">Nous enverrons un lien de reinitialisation si le compte existe.</p>
                <Button type="submit" className="btn-primary w-full" disabled={loading}>
                  {loading ? "Envoi..." : "Recevoir un lien"}
                </Button>
                <button type="button" className="support-text-link" onClick={() => setMode("login")}>Retour a la connexion</button>
              </form>
            )}

            {mode === "reset" && (
              <form onSubmit={handleResetPassword} className="support-form-stack">
                <input className="form-input" type="email" placeholder="Email" value={resetPasswordForm.email} onChange={(e) => setResetPasswordForm({ ...resetPasswordForm, email: e.target.value })} required />
                <input className="form-input" type="text" placeholder="Jeton de reinitialisation" value={resetPasswordForm.token} onChange={(e) => setResetPasswordForm({ ...resetPasswordForm, token: e.target.value })} required />
                <input className="form-input" type="password" placeholder="Nouveau mot de passe" value={resetPasswordForm.new_password} onChange={(e) => setResetPasswordForm({ ...resetPasswordForm, new_password: e.target.value })} required />
                <PasswordRequirements password={resetPasswordForm.new_password} />
                <Button type="submit" className="btn-primary w-full" disabled={loading}>
                  {loading ? "Reinitialisation..." : "Mettre a jour le mot de passe"}
                </Button>
                <button type="button" className="support-text-link" onClick={() => setMode("login")}>Retour a la connexion</button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </section>
  );

  const renderTicketsView = () => (
    <div className="support-work-grid">
      <Card className="support-content-card">
        <CardHeader>
          <CardTitle className="heading-2">
            {selectedClientId && user.role === "admin" ? "Tickets du client" : "Tickets recents"}
          </CardTitle>
        </CardHeader>
        <CardContent className="support-ticket-feed">
          {loadingData && <p className="caption">Chargement...</p>}
          {filteredTickets.map((ticketItem) => (
            <button
              key={ticketItem.id}
              type="button"
              className={`support-ticket-row ${selectedTicketId === ticketItem.id ? "is-active" : ""}`}
              onClick={() => openTicket(ticketItem.id)}
            >
              <div className="support-ticket-row-head">
                <strong>{ticketItem.ticket_number}</strong>
                <span>{ticketItem.status_label}</span>
              </div>
              <p>{ticketItem.title}</p>
              <small>
                {ticketItem.company_name} · Mis a jour le {formatDate(ticketItem.updated_at)}
              </small>
            </button>
          ))}
          {!loadingData && filteredTickets.length === 0 && (
            <p className="body-small">Aucun ticket a afficher.</p>
          )}
        </CardContent>
      </Card>

      <Card className="support-content-card">
        <CardHeader>
          <CardTitle className="heading-2">
            {selectedTicket ? selectedTicket.ticket_number : "Detail du ticket"}
          </CardTitle>
        </CardHeader>
        <CardContent className="support-ticket-detail">
          {!selectedTicket && <p className="body-small">Selectionne un ticket pour voir le detail.</p>}
          {selectedTicket && (
            <>
              <div className="support-ticket-meta">
                <span className="support-pill"><Clock3 className="w-4 h-4" />{selectedTicket.status_label}</span>
                <span className="support-pill"><FileText className="w-4 h-4" />{selectedTicket.category}</span>
                <span className="support-pill"><Mail className="w-4 h-4" />{selectedTicket.client_email}</span>
              </div>

              <div className="support-ticket-panel">
                <h3 className="heading-3">{selectedTicket.title}</h3>
                <p className="body-medium">{selectedTicket.description}</p>
              </div>

              <div className="support-ticket-panel">
                <h3 className="heading-3">Conversation</h3>
                {selectedTicket.messages.map((message) => (
                  <div key={message.id} className="support-message">
                    <div className="support-message-head">
                      <strong>{message.author_name}</strong>
                      <small>{formatDate(message.created_at)}</small>
                    </div>
                    <p>{message.content}</p>
                  </div>
                ))}
                <form onSubmit={handleSendMessage} className="support-form-stack">
                  <textarea className="form-input form-textarea" placeholder="Ajouter une reponse ou une precision" value={messageDraft} onChange={(e) => setMessageDraft(e.target.value)} required />
                  <Button type="submit" className="btn-secondary" disabled={loading}>Envoyer le message</Button>
                </form>
              </div>

              <div className="support-ticket-panel">
                <h3 className="heading-3">Pieces jointes</h3>
                {selectedTicket.attachments.length > 0 ? selectedTicket.attachments.map((attachment) => (
                  <div key={attachment.id} className="support-history-item">
                    <div>
                      <strong>{attachment.filename}</strong>
                      <p>{attachment.content_type} - {(attachment.size / 1024).toFixed(1)} Ko</p>
                    </div>
                    <div className="support-attachment-actions">
                      <small>{formatDate(attachment.uploaded_at)}</small>
                      <button type="button" className="btn-secondary" onClick={() => downloadAttachment(attachment)}>
                        Telecharger
                      </button>
                    </div>
                  </div>
                )) : <p className="body-small">Aucune piece jointe sur ce ticket.</p>}
              </div>

              <div className="support-ticket-panel">
                <h3 className="heading-3">Historique des statuts</h3>
                {selectedTicket.status_history.map((entry) => (
                  <div key={entry.id} className="support-history-item">
                    <div>
                      <strong>{entry.label}</strong>
                      <p>{entry.comment || "Sans commentaire"}</p>
                    </div>
                    <small>{entry.changed_by} - {formatDate(entry.changed_at)}</small>
                  </div>
                ))}
              </div>

              {user.role === "admin" && (
                <form onSubmit={handleStatusUpdate} className="support-admin-box">
                  <h3 className="heading-3">Pilotage du ticket</h3>
                  <select className="form-input" value={adminStatus} onChange={(e) => setAdminStatus(e.target.value)}>
                    {STATUS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                  <textarea className="form-input form-textarea" placeholder="Commentaire visible par le client" value={adminComment} onChange={(e) => setAdminComment(e.target.value)} />
                  <Button type="submit" className="btn-primary" disabled={loading}>Mettre a jour le statut</Button>
                </form>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );

  const renderNotificationsView = () => (
    <Card className="support-content-card">
      <CardHeader>
        <CardTitle className="heading-2">Notifications</CardTitle>
      </CardHeader>
      <CardContent className="support-sidebar-content">
        {notifications.map((notification) => (
          <button
            type="button"
            key={notification.id}
            className={`support-notification-item ${notification.read ? "" : "is-unread"}`}
            onClick={() => markAsRead(notification.id)}
          >
            <div className="support-notification-head">
              <strong>{notification.title}</strong>
              {!notification.read && <CircleAlert className="w-4 h-4" />}
            </div>
            <p>{notification.message}</p>
            <small>{formatDate(notification.created_at)}</small>
          </button>
        ))}
        {notifications.length === 0 && <p className="body-small">Aucune notification pour le moment.</p>}
      </CardContent>
    </Card>
  );

  const renderProfileView = () => (
    <Card className="support-content-card">
      <CardHeader>
        <CardTitle className="heading-2">{user.role === "admin" ? "Infos support" : "Infos client"}</CardTitle>
      </CardHeader>
      <CardContent className="support-profile-grid">
        <div className="support-info-card">
          <span className="caption">Nom</span>
          <strong>{user.full_name}</strong>
        </div>
        <div className="support-info-card">
          <span className="caption">Entreprise</span>
          <strong>{user.company_name}</strong>
        </div>
        <div className="support-info-card">
          <span className="caption">Email</span>
          <strong>{user.email}</strong>
        </div>
        <div className="support-info-card">
          <span className="caption">Tickets</span>
          <strong>{tickets.length}</strong>
        </div>
      </CardContent>
    </Card>
  );

  const renderNewTicketView = () => (
    <Card className="support-content-card">
      <CardHeader>
        <CardTitle className="heading-2">Nouvelle demande de modification</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleCreateTicket} className="support-form-grid">
          <input className="form-input" type="text" placeholder="Titre de la demande" value={ticketForm.title} onChange={(e) => setTicketForm({ ...ticketForm, title: e.target.value })} required />
          <select className="form-input" value={ticketForm.category} onChange={(e) => setTicketForm({ ...ticketForm, category: e.target.value })}>
            <option value="modification-contenu">Modification de contenu</option>
            <option value="ajout-page">Ajout de page</option>
            <option value="correction-technique">Correction technique</option>
            <option value="seo">SEO</option>
          </select>
          <input className="form-input support-span-2" type="url" placeholder="URL du site concerne" value={ticketForm.site_url} onChange={(e) => setTicketForm({ ...ticketForm, site_url: e.target.value })} required />
          <textarea className="form-input form-textarea support-span-2" placeholder="Expliquez precisement la modification souhaitee" value={ticketForm.description} onChange={(e) => setTicketForm({ ...ticketForm, description: e.target.value })} required />
          <div className="support-span-2">
            <label className="form-label">Pieces jointes (3 max, PDF/JPG/PNG/DOCX)</label>
            <input className="form-input" type="file" multiple accept=".pdf,.png,.jpg,.jpeg,.docx" onChange={(e) => setTicketFiles(e.target.files)} />
          </div>
          <Button type="submit" className="btn-primary support-span-2" disabled={loading}>
            {loading ? "Envoi..." : "Envoyer le ticket"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );

  const renderClientsView = () => (
    <Card className="support-content-card">
      <CardHeader>
        <CardTitle className="heading-2">Clients</CardTitle>
      </CardHeader>
      <CardContent className="support-clients-grid">
        {adminClients.map((client) => (
          <button
            type="button"
            key={client.id}
            className={`support-client-card ${selectedClientId === client.id ? "is-active" : ""}`}
            onClick={() => {
              setSelectedClientId((current) => (current === client.id ? "" : client.id));
              setActiveView("tickets");
            }}
          >
            <div className="support-ticket-row-head">
              <strong>{client.company_name}</strong>
              <span>{client.open_ticket_count} ouverts</span>
            </div>
            <p>{client.full_name}</p>
            <small>{client.email}</small>
            <small>{client.ticket_count} ticket(s)</small>
          </button>
        ))}
        {adminClients.length === 0 && <p className="body-small">Aucun client pour le moment.</p>}
      </CardContent>
    </Card>
  );

  const renderConnectedView = () => (
    <section className="support-shell">
      <div className="container support-app-layout">
        <aside className="support-app-sidebar">
          <div className="support-app-sidebar-top">
            <div className="support-app-brand">
              <span className="support-app-brand-mark">{user.role === "admin" ? "A" : "C"}</span>
              <div>
                <strong>{user.company_name}</strong>
                <p>{user.full_name}</p>
              </div>
            </div>

            <nav className="support-nav">
              {menuItems.map((item) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={`support-nav-item ${activeView === item.id ? "is-active" : ""}`}
                    onClick={() => setActiveView(item.id)}
                  >
                    <span className="support-nav-item-main">
                      <Icon className="w-4 h-4" />
                      {item.label}
                    </span>
                    {item.badge > 0 && <span className="support-nav-badge">{item.badge}</span>}
                  </button>
                );
              })}
            </nav>
          </div>

          <div className="support-app-sidebar-bottom">
            <button type="button" className="support-nav-item" onClick={handleLogout}>
              <span className="support-nav-item-main">
                <LogOut className="w-4 h-4" />
                Deconnexion
              </span>
            </button>
          </div>
        </aside>

        <main className="support-app-main">
          <div className="support-app-main-header">
            <div>
              <h1 className="heading-2">
                {activeView === "tickets" && "Tickets"}
                {activeView === "new-ticket" && "Ajouter un ticket"}
                {activeView === "notifications" && "Notifications"}
                {activeView === "profile" && (user.role === "admin" ? "Infos support" : "Infos client")}
                {activeView === "clients" && "Clients"}
              </h1>
              <p className="body-small">
                {activeView === "tickets" && "Du plus recent au moins recent"}
                {activeView === "clients" && "Vue admin pour gerer les clients et filtrer leurs tickets"}
                {activeView === "notifications" && "Suivi des alertes de l'espace support"}
                {activeView === "new-ticket" && "Creation d'une nouvelle demande"}
                {activeView === "profile" && "Informations du compte courant"}
              </p>
            </div>
            {selectedClientId && activeView === "tickets" && user.role === "admin" && (
              <button type="button" className="btn-secondary" onClick={() => setSelectedClientId("")}>
                Tous les clients
              </button>
            )}
          </div>

          {activeView === "tickets" && renderTicketsView()}
          {activeView === "new-ticket" && user.role === "client" && renderNewTicketView()}
          {activeView === "notifications" && renderNotificationsView()}
          {activeView === "profile" && renderProfileView()}
          {activeView === "clients" && user.role === "admin" && renderClientsView()}
        </main>
      </div>
    </section>
  );

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-page)" }}>
      <Header />
      {!user ? renderAuthView() : renderConnectedView()}
    </div>
  );
}
